import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export const ReviewResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const round = parseInt(searchParams.get('round') || '1');
  const total = parseInt(searchParams.get('total') || '0');
  const remembered = parseInt(searchParams.get('remembered') || '0');
  const needReview = parseInt(searchParams.get('needReview') || '0');
  
  const accuracy = total > 0 ? Math.round((remembered / total) * 100) : 0;
  const hasProblemsForNextRound = needReview > 0;

  const handleStartNextRound = () => {
    navigate(`/reviews?round=${round + 1}`);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleStartNewReview = () => {
    // Clear previous round data and start fresh
    localStorage.removeItem(`review_round_${round}`);
    navigate('/reviews?round=1');
  };

  return (
    <div className="review-results-page">
      <div className="results-container">
        <div className="results-header">
          <h1>📊 Review Session Complete!</h1>
          <div className="round-info">Round {round} Results</div>
        </div>

        <div className="results-stats">
          <div className="stat-card total">
            <div className="stat-number">{total}</div>
            <div className="stat-label">Total Problems</div>
          </div>
          <div className="stat-card remembered">
            <div className="stat-number">{remembered}</div>
            <div className="stat-label">Remembered</div>
          </div>
          <div className="stat-card need-review">
            <div className="stat-number">{needReview}</div>
            <div className="stat-label">Need Review</div>
          </div>
          <div className="stat-card accuracy">
            <div className="stat-number">{accuracy}%</div>
            <div className="stat-label">Accuracy</div>
          </div>
        </div>

        <div className="results-chart">
          <div className="chart-container">
            <div className="chart-bar">
              <div 
                className="chart-fill remembered-fill"
                style={{ width: `${(remembered / total) * 100}%` }}
              ></div>
              <div 
                className="chart-fill need-review-fill"
                style={{ width: `${(needReview / total) * 100}%` }}
              ></div>
            </div>
            <div className="chart-labels">
              <span className="label remembered-label">Remembered ({remembered})</span>
              <span className="label need-review-label">Need Review ({needReview})</span>
            </div>
          </div>
        </div>

        <div className="results-message">
          {accuracy >= 80 ? (
            <div className="success-message">
              <div className="message-icon">🎉</div>
              <div className="message-text">
                <h3>Excellent Work!</h3>
                <p>You remembered {accuracy}% of the problems. You're mastering these Meta interview questions!</p>
              </div>
            </div>
          ) : accuracy >= 60 ? (
            <div className="good-message">
              <div className="message-icon">👍</div>
              <div className="message-text">
                <h3>Good Progress!</h3>
                <p>You remembered {accuracy}% of the problems. Keep reviewing to improve your retention.</p>
              </div>
            </div>
          ) : (
            <div className="improvement-message">
              <div className="message-icon">💪</div>
              <div className="message-text">
                <h3>Keep Going!</h3>
                <p>You remembered {accuracy}% of the problems. More practice will help you master these concepts.</p>
              </div>
            </div>
          )}
        </div>

        <div className="results-actions">
          {hasProblemsForNextRound && (
            <button
              className="btn btn-primary action-btn"
              onClick={handleStartNextRound}
            >
              🔄 Start Round {round + 1}
              <span className="btn-subtitle">Review {needReview} problems that need work</span>
            </button>
          )}
          
          <button
            className="btn btn-secondary action-btn"
            onClick={handleStartNewReview}
          >
            🔄 Start New Review Session
            <span className="btn-subtitle">Start over with all Meta problems</span>
          </button>
          
          <button
            className="btn btn-outline action-btn"
            onClick={handleBackToHome}
          >
            🏠 Back to Home
            <span className="btn-subtitle">Continue practicing problems</span>
          </button>
        </div>

        <div className="review-tips">
          <h3>💡 Review Tips</h3>
          <ul>
            <li>Focus on understanding the patterns, not just memorizing solutions</li>
            <li>Practice explaining your approach out loud</li>
            <li>Review problems you struggled with more frequently</li>
            <li>Time yourself to simulate real interview conditions</li>
          </ul>
        </div>
      </div>
    </div>
  );
};